import { useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-types
export type DelayedFunction = (cb: Function, name: string) => void;

/*
 * Introduces an artificial delay before an event happens to help prevent unintentional user action (i.e. briefly mousing over an
 * icon the user doesn't mean to interact with). If a user triggers a different event before the timeout is hit, the first action is
 * cancelled.
 */
const useInteractionDelay = (
  { initialDelay, subsequentDelay } = {
    initialDelay: 100,
    subsequentDelay: 300,
  }
): [DelayedFunction, () => void] => {
  const timeoutRef = useRef(null);

  const delay: DelayedFunction = (callback, openedTab) => {
    // if no tabs are hovered, activate the hover in 0.1 seconds. if a hover state is already active,
    // wait 0.5 seconds before switching hovers
    const timeout = !openedTab ? initialDelay : subsequentDelay;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callback();
      timeoutRef.current = null;
    }, timeout);
  };

  const cancel = () => {
    clearTimeout(timeoutRef.current);
  };

  return [delay, cancel];
};

export default useInteractionDelay;
